import { useTranslation } from 'react-i18next';
import type { Control } from 'react-hook-form';
import {
  cn,
  Flex,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Checkbox,
  Markdown,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Text,
} from '@kognitos/lattice';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import {
  parseConfigMapFields,
  hasConfigMap,
  hasRequiredConfigMapFields,
} from '../utils';

export interface IConfigMapStepProps {
  /** The book descriptor containing config_map */
  integration: Bdkv2BookDescriptor;
  /** React Hook Form control - accepts any form control type */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  /** Optional prefix for field names (e.g., 'draftConfig' or 'publishedConfig') */
  fieldPrefix?: string;
  /** Whether fields are disabled (e.g., when syncing from draft) */
  disabled?: boolean;
  /** Whether fields are read-only (e.g., in view mode) */
  readOnly?: boolean;
  /** Whether to show section header */
  showHeader?: boolean;
}

/**
 * Renders form fields for config_map entries from a book descriptor.
 * Supports text, number, boolean (checkbox), and enum (select) field types.
 *
 * @param props - Component props
 * @returns JSX element with config_map form fields, or null if no config_map
 */
export function ConfigMapStep({
  integration,
  control,
  fieldPrefix = '',
  disabled = false,
  readOnly = false,
  showHeader = true,
}: IConfigMapStepProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  // Early return if no config_map
  if (!hasConfigMap(integration)) {
    return null;
  }

  const fields = parseConfigMapFields(integration.config_map);

  if (fields.length === 0) {
    return null;
  }

  // Most integrations only expose tuning knobs here, so the section is labelled
  // "(optional)". Some put a genuinely mandatory field in the same block
  // (Anthropic requires `model`), and calling that optional tells the user they
  // can skip a field the form will then reject — the required marker on the
  // field and the heading were contradicting each other.
  const sectionTitleKey = hasRequiredConfigMapFields(fields)
    ? 'integrations.configMap.sectionTitle'
    : 'integrations.configMap.sectionTitleOptional';

  /**
   * Renders the appropriate input component based on field type.
   */
  const renderFieldInput = (
    field: (typeof fields)[0],
    formField: {
      value: string | number | boolean;
      onChange: (value: string | number | boolean) => void;
      onBlur: () => void;
      name: string;
    },
  ) => {
    const { fieldType, enumOptions, label } = field;

    switch (fieldType) {
      case 'boolean':
        return (
          <Checkbox
            checked={Boolean(formField.value)}
            onCheckedChange={formField.onChange}
            disabled={disabled || readOnly}
            data-testid={`config-input-${field.key}`}
          />
        );

      case 'enum':
        return (
          <Select
            value={String(formField.value || '')}
            onValueChange={formField.onChange}
            disabled={disabled || readOnly}
          >
            <SelectTrigger
              data-testid={`config-input-${field.key}`}
              className="w-full"
            >
              <SelectValue
                placeholder={tIntegrations(
                  'integrations.configMap.selectPlaceholder',
                  {
                    fieldLabel: label,
                  },
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {enumOptions?.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={tIntegrations('integrations.configMap.placeholder', {
              fieldLabel: label,
            })}
            className="w-full"
            disabled={disabled}
            readOnly={!disabled && readOnly}
            name={formField.name}
            onBlur={formField.onBlur}
            value={formField.value === undefined ? '' : String(formField.value)}
            onChange={e => {
              const val = e.target.value;
              // Keep as string in form, will be converted to number on submit
              formField.onChange(val);
            }}
            data-testid={`config-input-${field.key}`}
          />
        );

      case 'text':
      default:
        return (
          <Input
            type="text"
            placeholder={tIntegrations('integrations.configMap.placeholder', {
              fieldLabel: label,
            })}
            className="w-full"
            disabled={disabled}
            readOnly={!disabled && readOnly}
            name={formField.name}
            onBlur={formField.onBlur}
            value={formField.value === undefined ? '' : String(formField.value)}
            onChange={e => formField.onChange(e.target.value)}
            data-testid={`config-input-${field.key}`}
          />
        );
    }
  };

  return (
    <Flex direction="column" gap={4} className="w-full">
      {showHeader && (
        <>
          <Separator />
          <Text weight="medium" level="small">
            {tIntegrations(sectionTitleKey)}
          </Text>
        </>
      )}

      <div className="space-y-4 w-full">
        {fields.map(field => {
          const fullFieldName = fieldPrefix
            ? `${fieldPrefix}.${field.key}`
            : field.key;

          return (
            <FormField
              key={fullFieldName}
              control={control}
              name={fullFieldName}
              render={({ field: formField }) => (
                <FormItem
                  className={cn(
                    field.fieldType === 'boolean' &&
                      'flex flex-row items-start space-x-3 space-y-0',
                  )}
                >
                  {field.fieldType === 'boolean' ? (
                    <>
                      <FormControl>
                        {renderFieldInput(field, formField)}
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          <Flex align="center" gap={2}>
                            {field.label}
                            {!field.isOptional && (
                              <span className="text-destructive">*</span>
                            )}
                            {disabled && (
                              <span className="text-xs text-muted-foreground leading-[inherit]">
                                {tIntegrations(
                                  'integrations.connections.copiedFromDraft',
                                )}
                              </span>
                            )}
                          </Flex>
                        </FormLabel>
                        {field.description && (
                          <FormDescription>
                            <Markdown
                              textProps={{
                                level: 'small',
                                color: 'muted',
                                weight: 'normal',
                              }}
                            >
                              {field.description}
                            </Markdown>
                          </FormDescription>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <FormLabel>
                        <Flex align="center" gap={2}>
                          {field.label}
                          {!field.isOptional && (
                            <span className="text-destructive">*</span>
                          )}
                          {disabled && (
                            <span className="text-xs text-muted-foreground leading-[inherit]">
                              {tIntegrations(
                                'integrations.connections.copiedFromDraft',
                              )}
                            </span>
                          )}
                        </Flex>
                      </FormLabel>
                      <FormControl>
                        {renderFieldInput(field, formField)}
                      </FormControl>
                      {field.description && (
                        <FormDescription>
                          <Markdown
                            textProps={{
                              level: 'small',
                              color: 'muted',
                              weight: 'normal',
                            }}
                          >
                            {field.description}
                          </Markdown>
                        </FormDescription>
                      )}
                    </>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          );
        })}
      </div>
    </Flex>
  );
}
